import type { LocalSpeechInputAdapter } from './direct-call-input';
import type { DirectCallDiagnostics } from './direct-call-diagnostics';

/** Reports adapter boundaries; permission and native mic details stay inside the native adapter. */
export function diagnosticInput(
  input: LocalSpeechInputAdapter,
  diagnostics: DirectCallDiagnostics,
  now: () => number = () => performance.now()
): LocalSpeechInputAdapter {
  return {
    canPause: input.canPause,
    async start(signal) {
      const started = now();
      diagnostics.record('capture-start', 'started');
      try {
        const recording = await input.start(
          signal,
          (stage, outcome, code, duration) =>
            diagnostics.record(stage, outcome, code, duration)
        );
        diagnostics.record('mic-open', 'succeeded', 'none', now() - started);
        let finished = false;
        return {
          interrupted: recording.interrupted,
          interruptionCause: () => recording.interruptionCause?.() ?? null,
          inputLevel: recording.inputLevel
            ? () => recording.inputLevel!()
            : undefined,
          // US 35617 AC2: pass a false mute's pause/resume through.
          ...(recording.pause && recording.resume
            ? {
                pause: () => recording.pause!(),
                resume: () => recording.resume!(),
              }
            : {}),
          async finish() {
            const finishStarted = now();
            // finish is a combined stop-and-local-transcription boundary.
            diagnostics.record('transcribe', 'started');
            try {
              const text = await recording.finish();
              finished = true;
              diagnostics.record(
                'transcribe',
                'succeeded',
                'none',
                now() - finishStarted
              );
              return text;
            } catch (error) {
              diagnostics.record(
                'transcribe',
                signal.aborted ? 'cancelled' : 'failed',
                signal.aborted ? 'cancelled' : 'unknown-failure',
                now() - finishStarted
              );
              throw error;
            }
          },
          cancel() {
            try {
              recording.cancel();
            } finally {
              if (!finished && !input.reportsCaptureDiagnostics)
                diagnostics.record('capture-stop', 'cancelled', 'cancelled');
              finished = true;
            }
          },
        };
      } catch (error) {
        diagnostics.record(
          'capture-start',
          signal.aborted ? 'cancelled' : 'failed',
          signal.aborted ? 'cancelled' : 'unknown-failure',
          now() - started
        );
        throw error;
      }
    },
  };
}
